import { DateTime } from "luxon";

import {
  IUpsertWasteReceiptDraft,
  IWasteReceiptConsignmentScan,
  IWasteReceiptConsignmentScanResult,
  IWasteReceiptRow,
  IWasteReceiptSubmitResult,
  WasteReceiptAiField,
  WasteReceiptCarrierType,
  WasteReceiptScanStatus,
} from "../models/wasteTracking/IWasteReceiptRow";

/** Matches WasteReceiptRules.MaxSubmitBatch on the API. Submitting only queues, so this is just a request-size cap. */
export const WASTE_RECEIPT_SUBMIT_CHUNK = 500;

const LOCAL_INPUT_FORMAT = "yyyy-LL-dd'T'HH:mm";
const LONDON = "Europe/London";

export function chunkIds<T>(ids: T[], size = WASTE_RECEIPT_SUBMIT_CHUNK): T[][] {
  if (size <= 0) throw new Error("chunk size must be positive");
  const chunks: T[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/** API UTC ISO -> value for a datetime-local input, in London time. */
export function toLocalDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(LONDON);
  return dt.isValid ? dt.toFormat(LOCAL_INPUT_FORMAT) : "";
}

/** datetime-local input value (London) -> UTC ISO for the API; null when blank/invalid. */
export function fromLocalDateTimeInput(value: string | null | undefined): string | null {
  if (!value) return null;
  const dt = DateTime.fromFormat(value, LOCAL_INPUT_FORMAT, { zone: LONDON });
  return dt.isValid ? dt.toUTC().toISO() : null;
}

/**
 * The full editable set for a single-row save. The API treats this as a replace, so every
 * field is sent - including the defaults the row was rendered with, which is what the user
 * sees and therefore what they are confirming.
 */
export function rowToDraft(row: IWasteReceiptRow): IUpsertWasteReceiptDraft {
  return {
    dateTimeReceived: row.dateTimeReceived,
    weightKg: row.weightKg,
    weightIsEstimate: row.weightIsEstimate,
    hazardousWasteConsignmentCode: row.hazardousWasteConsignmentCode,
    carrierType: row.carrierType,
    carrierRegistrationNumber: row.carrierRegistrationNumber,
    carrierOrganisationName: row.carrierOrganisationName,
    carrierVehicleRegistration: row.carrierVehicleRegistration,
    recoveryCode: row.recoveryCode,
    haulierAccountId: row.haulierAccountId,
  };
}

/** Form shape of the bulk update modal - strings because they come straight off inputs. */
export interface IWasteReceiptBulkFormValues {
  useCollectedDate: boolean;
  dateTimeReceived: string;
  weightKg: string;
  weightIsEstimate: "" | "true" | "false";
  hazardousWasteConsignmentCode: string;
  carrierType: "" | "0" | "1" | "2";
  carrierRegistrationNumber: string;
  carrierOrganisationName: string;
  carrierVehicleRegistration: string;
  recoveryCode: string;
  /** Haulier account id; picking one makes every selected row a third-party movement. */
  haulierAccountId: string;
}

export const emptyBulkFormValues: IWasteReceiptBulkFormValues = {
  useCollectedDate: false,
  dateTimeReceived: "",
  weightKg: "",
  weightIsEstimate: "",
  hazardousWasteConsignmentCode: "",
  carrierType: "",
  carrierRegistrationNumber: "",
  carrierOrganisationName: "",
  carrierVehicleRegistration: "",
  recoveryCode: "",
  haulierAccountId: "",
};

const blankToUndefined = (value: string | undefined | null) => {
  const trimmed = (value ?? "").trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

/**
 * Blank means "leave unchanged", so only fields the user actually filled in make it into the
 * patch. Returns null when nothing was set so the caller can refuse an empty update.
 */
export function buildBulkPatch(values: IWasteReceiptBulkFormValues): IUpsertWasteReceiptDraft | null {
  const patch: IUpsertWasteReceiptDraft = {};

  if (values.useCollectedDate) {
    patch.useCollectedDateAsReceived = true;
  } else {
    const received = fromLocalDateTimeInput(values.dateTimeReceived);
    if (received) patch.dateTimeReceived = received;
  }

  const weight = blankToUndefined(values.weightKg);
  if (weight !== undefined) {
    const parsed = Number(weight);
    if (Number.isFinite(parsed) && parsed > 0) patch.weightKg = parsed;
  }

  if (values.weightIsEstimate === "true") patch.weightIsEstimate = true;
  if (values.weightIsEstimate === "false") patch.weightIsEstimate = false;

  const consignment = blankToUndefined(values.hazardousWasteConsignmentCode);
  if (consignment !== undefined) patch.hazardousWasteConsignmentCode = consignment.toUpperCase();

  if (values.carrierType !== "") {
    patch.carrierType = Number(values.carrierType) as WasteReceiptCarrierType;
  }

  const carrierReg = blankToUndefined(values.carrierRegistrationNumber);
  if (carrierReg !== undefined) patch.carrierRegistrationNumber = carrierReg.toUpperCase();

  const carrierName = blankToUndefined(values.carrierOrganisationName);
  if (carrierName !== undefined) patch.carrierOrganisationName = carrierName;

  const truckReg = blankToUndefined(values.carrierVehicleRegistration);
  if (truckReg !== undefined) patch.carrierVehicleRegistration = truckReg.toUpperCase();

  const recovery = blankToUndefined(values.recoveryCode);
  if (recovery !== undefined) patch.recoveryCode = recovery.toUpperCase();

  // The API forces third-party on a haulier patch, so a conflicting carrier type is dropped here
  // rather than sent and silently overridden.
  const haulierAccountId = blankToUndefined(values.haulierAccountId);
  if (haulierAccountId !== undefined) {
    patch.haulierAccountId = haulierAccountId;
    patch.carrierType = WasteReceiptCarrierType.ThirdPartyCarrier;
  }

  return Object.keys(patch).length === 0 ? null : patch;
}

export interface IWasteReceiptSubmitSummary {
  total: number;
  /** Accepted onto the API's background queue; DEFRA is called afterwards, one vehicle at a time. */
  queued: number;
  submitted: number;
  failed: number;
  skipped: number;
  /** Failed + skipped rows with their messages, in the order they came back. */
  problems: { vehicleId: string; vrm: string; skipped: boolean; messages: string[] }[];
  /** Kept empty. DEFRA recommendations on an accepted receipt are not shown. */
  warnings: { vehicleId: string; vrm: string; wasteTrackingId: string | null; messages: string[] }[];
}

export function summariseSubmitResults(results: IWasteReceiptSubmitResult[]): IWasteReceiptSubmitSummary {
  const summary: IWasteReceiptSubmitSummary = {
    total: results.length,
    queued: 0,
    submitted: 0,
    failed: 0,
    skipped: 0,
    problems: [],
    warnings: [],
  };

  for (const result of results) {
    const vrm = result.vrm ?? "Unknown VRM";

    // Not a success yet, and not a problem either: DEFRA's answer lands on the row later.
    if (result.queued) {
      summary.queued++;
      continue;
    }

    if (result.success) {
      summary.submitted++;
      continue;
    }

    if (result.skipped) summary.skipped++;
    else summary.failed++;

    summary.problems.push({
      vehicleId: result.vehicleId,
      vrm,
      skipped: result.skipped,
      messages: result.errors?.length ? result.errors : ["No detail returned"],
    });
  }

  return summary;
}

export const carrierTypeLabels: Record<WasteReceiptCarrierType, string> = {
  [WasteReceiptCarrierType.OwnCarrier]: "Own carrier",
  [WasteReceiptCarrierType.HouseholdDropOff]: "Householder drop-off",
  [WasteReceiptCarrierType.ThirdPartyCarrier]: "Third-party carrier",
};

// ---- Consignment note scan (yard photo, AI read, office confirm) ----

/** True when the yard has captured a note for this row, whatever the AI made of it. */
export function hasConsignmentNote(row: Pick<IWasteReceiptRow, "consignmentScan">): boolean {
  return !!row.consignmentScan && row.consignmentScan.status !== WasteReceiptScanStatus.None;
}

/** The AI's read is on the row and nobody has confirmed it yet - the row is held until they do. */
export function scanNeedsReview(row: Pick<IWasteReceiptRow, "consignmentScan">): boolean {
  return row.consignmentScan?.status === WasteReceiptScanStatus.AwaitingReview;
}

/** True when this cell's current value came from the AI and is still unconfirmed. */
export function isAiField(row: Pick<IWasteReceiptRow, "consignmentScan">, field: WasteReceiptAiField): boolean {
  return scanNeedsReview(row) && (row.consignmentScan?.aiFields ?? []).includes(field);
}

/** Short label for the badge under the consignment note cell; null when there is nothing to show. */
export function scanStatusLabel(scan: IWasteReceiptConsignmentScan | null | undefined): string | null {
  switch (scan?.status) {
    case WasteReceiptScanStatus.Pending:
      return "Reading note…";
    case WasteReceiptScanStatus.AwaitingReview:
      return "AI read - review";
    case WasteReceiptScanStatus.Confirmed:
      return "Note confirmed";
    case WasteReceiptScanStatus.Failed:
      return "Note not read";
    default:
      return null;
  }
}

/** "160104" -> "16 01 04" for display; anything that isn't six digits is left alone. */
export function formatEwcCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const digits = code.replace(/\D/g, "");
  if (digits.length !== 6) return code;
  return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)}`;
}

export interface IHaulierPreset {
  name: string;
  carrierRegistrationNumber: string | null;
  haulierVehicleRegistration: string | null;
}

/**
 * What to pre-fill the new haulier account form with when the note names a carrier we don't
 * have. Null when the AI read no carrier, when the carrier is us, or when an account already
 * matched - there is nothing to create in those cases.
 */
export function haulierPresetFromScan(result: IWasteReceiptConsignmentScanResult | null | undefined): IHaulierPreset | null {
  if (!result || result.carrierIsOwn || result.haulierMatch) return null;
  const carrier = result.carrier;
  if (!carrier) return null;

  const name = (carrier.organisationName ?? "").trim();
  const registration = (carrier.registrationNumber ?? "").trim().toUpperCase();
  if (!name && !registration) return null;

  return {
    name: name || registration,
    carrierRegistrationNumber: registration || null,
    haulierVehicleRegistration: (carrier.vehicleRegistration ?? "").trim().toUpperCase() || null,
  };
}
